import React, { createContext, useContext, useEffect, useState, useCallback } from "react";

const STORAGE_KEY = "srt_edits_v1";
const ADMIN_KEY = "srt_admin_mode_v1";

const EditContext = createContext(null);

export const EditProvider = ({ children }) => {
  const [editMode, setEditMode] = useState(false);
  const [adminMode, setAdminMode] = useState(false);
  const [edits, setEdits] = useState({});

  // On mount: detect ?edit=1 in URL or existing localStorage flag
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) setEdits(JSON.parse(raw));
    } catch (e) { /* ignore */ }

    try {
      const params = new URLSearchParams(window.location.search);
      const hasQuery = params.get("edit") === "1" || window.location.hash === "#admin";
      const hasFlag  = localStorage.getItem(ADMIN_KEY) === "1";
      if (hasQuery || hasFlag) {
        setAdminMode(true);
        localStorage.setItem(ADMIN_KEY, "1");
        // Clean the URL so ?edit=1 doesn't stay visible after activation
        if (hasQuery) {
          const url = new URL(window.location.href);
          url.searchParams.delete("edit");
          if (url.hash === "#admin") url.hash = "";
          window.history.replaceState({}, "", url.toString());
        }
      }
    } catch (e) { /* ignore */ }
  }, []);

  const lockAdmin = useCallback(() => {
    try { localStorage.removeItem(ADMIN_KEY); } catch (e) { /* ignore */ }
    setAdminMode(false);
    setEditMode(false);
  }, []);

  const persist = useCallback((next) => {
    setEdits(next);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch (e) { /* ignore */ }
  }, []);

  const setValue = useCallback((id, value) => {
    persist({ ...edits, [id]: value });
  }, [edits, persist]);

  const getValue = useCallback((id, fallback) => {
    if (edits && Object.prototype.hasOwnProperty.call(edits, id)) return edits[id];
    return fallback;
  }, [edits]);

  const resetAll = useCallback(() => {
    persist({});
  }, [persist]);

  const exportJson = useCallback(() => {
    const blob = new Blob([JSON.stringify(edits, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "srt-content-edits.json";
    a.click();
    URL.revokeObjectURL(url);
  }, [edits]);

  return (
    <EditContext.Provider value={{ editMode, setEditMode, getValue, setValue, resetAll, exportJson, edits, adminMode, lockAdmin }}>
      {children}
    </EditContext.Provider>
  );
};

export const useEdit = () => {
  const ctx = useContext(EditContext);
  if (!ctx) throw new Error("useEdit must be used inside EditProvider");
  return ctx;
};
